//== input listeners: DOM → state.
//== one-way wiring; render.js handles state → DOM. text inputs reformat their
//== own value on blur here, exactly as the redemption calculator does.
//==
//== each bind* returns { commit, reset }. initInputs collects them and exposes
//== resetInputs() for the start-over handler, so DOM values and state both
//== return to "as just loaded" - store.reset alone only clears state, leaving
//== stale text in the fields - plus commitInputs() for the Result button.
//==
//== there is no navigate.js in this calculator - one screen, nothing to
//== navigate - so the [data-svc-action] buttons are wired here.

import { parseMoney, formatMoney, parsePercent, formatPercent } from '../calc-shared/format.js'
import { validate } from './validate.js'

export function initInputs(store, root) {
  const bindings = []
  const push = (binding) => { if (binding) bindings.push(binding) }

  push(bindMoneyInput(store, root, '#svcPurchasePrice', 'purchasePrice'))
  push(bindMoneyInput(store, root, '#svcOriginalEquity', 'originalEquity'))
  push(bindPercentInput(store, root, '#svcCurrentSharePct', 'currentEquityPct'))

  function resetInputs() {
    bindings.forEach((binding) => binding.reset())
  }

  //== read every field's current text into state, not only the ones blur has
  //== already been through: Safari does not focus a button on click, so the
  //== field the customer was typing in may never fire blur before Result runs.
  //== each commit also marks its own field touched, so this doubles as the
  //== "the customer says they are done" pass that lets every error show.
  function commitInputs() {
    bindings.forEach((binding) => binding.commit())
  }

  bindActions(store, root, { resetInputs, commitInputs })

  return { resetInputs, commitInputs }
}

function bindMoneyInput(store, root, selector, key) {
  return bindTextInput(store, root, selector, key, parseMoney, formatMoney)
}

function bindPercentInput(store, root, selector, key) {
  return bindTextInput(store, root, selector, key, parsePercent, formatPercent)
}

//== blur rather than input: the customer is mid-typing until they leave the
//== field, and reformatting on every keystroke fights the caret. it also means
//== an error can only appear once the field has been left (see store.touch).
function bindTextInput(store, root, selector, key, parse, format) {
  let input = root.querySelector(selector)
  if (!input) return null
  const commit = () => {
    const num = parse(input.value)
    store.setState({ inputs: { [key]: num } })
    store.touch(key)
    input.value = format(num)
  }
  input.addEventListener('blur', commit)
  return {
    commit,
    reset() {
      input = replaceWithFreshClone(input)
      input.addEventListener('blur', commit)
    },
  }
}

//== one delegated listener on the root, so buttons can be moved or duplicated
//== in the markup without rebinding
function bindActions(store, root, { resetInputs, commitInputs }) {
  root.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-svc-action]')
    if (!trigger || !root.contains(trigger)) return

    if (trigger.dataset.svcAction === 'start-over') {
      store.reset()
      resetInputs()
      return
    }

    //== Result does not unlock anything render.js was withholding - the table
    //== already appears as soon as the form is valid, by blur alone. its job is
    //== to give the customer an explicit way to finish the form, and to take
    //== them to the outcome (client request 2026-08-28).
    if (trigger.dataset.svcAction === 'result') {
      commitInputs()
      focusOutcome(store, root)
    }
  })
}

//== send the customer to whatever the press produced: the results panel, or the
//== first field standing in its way. without this the button looks inert on a
//== narrow screen, where the panel sits below the whole form.
function focusOutcome(store, root) {
  const { valid, errors } = validate(store.getState())
  const target = valid
    ? root.querySelector('[data-svc-focus="result"]')
    : firstInvalidInput(root, errors)
  if (!target) return
  //== focus first scrolls its own way, so suppress that and do the scrolling here
  target.focus({ preventScroll: true })
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' })
}

//== message slots are queried in DOM order and each one names its input through
//== the aria-describedby already in the markup, so no field-to-selector map is
//== duplicated here - same approach as render.js's markInvalid
function firstInvalidInput(root, errors) {
  const slots = Array.from(root.querySelectorAll('[data-svc-error]'))
  for (const slot of slots) {
    if (!errors[slot.dataset.svcError] || !slot.id) continue
    const input = root.querySelector(`[aria-describedby~="${slot.id}"]`)
    if (input) return input
  }
  return null
}

//== swap a required text input for a parser-fresh element so the browser's
//== sticky "user-interacted" flag is dropped - otherwise :user-invalid keeps
//== matching after start-over even with the value cleared. cloneNode preserves
//== the flag in some engines; re-parsing via <template> guarantees fresh
//== internal state. returns the new node so callers can re-bind.
function replaceWithFreshClone(input) {
  const tmpl = document.createElement('template')
  tmpl.innerHTML = input.outerHTML
  const fresh = tmpl.content.firstElementChild
  fresh.value = ''
  input.replaceWith(fresh)
  return fresh
}
